import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { contentService, budgetService } from '../../services/apiService';
import { toast } from 'react-toastify';
import { FiCopy, FiDownload, FiRefreshCw, FiStar, FiAlertTriangle, FiAlertCircle } from 'react-icons/fi';
import { useT } from '../../utils/i18n';

const CONTENT_TYPES = {
  anchor_script: { labelKey: 'type.anchor_script', duration: 400 },
  story: { labelKey: 'type.story', duration: 1500 },
  debate: { labelKey: 'type.debate', duration: 2500 },
  breaking_news: { labelKey: 'type.breaking_news', duration: 300 },
  bulletin: { labelKey: 'type.bulletin', duration: 150 },
  facebook_post: { labelKey: 'type.facebook_post', duration: 150 },
  youtube: { labelKey: 'type.youtube', duration: 250 },
};

const TONES = ['formal', 'conversational', 'urgent', 'analytical', 'empathetic', 'happy', 'angry', 'sad', 'dramatic', 'neutral'];

const URL_TO_TYPE = {
  'anchor-script': 'anchor_script',
  'story': 'story',
  'debate': 'debate',
  'breaking-news': 'breaking_news',
  'bulletin': 'bulletin',
  'facebook-post': 'facebook_post',
  'youtube': 'youtube',
};

// Parse the structured YouTube output from the AI into labelled sections
const parseYouTubeOutput = (raw) => {
  if (!raw) return null;
  const get = (label, nextLabel) => {
    const re = new RegExp(`${label}:\\s*([\\s\\S]*?)(?=${nextLabel}:|$)`, 'i');
    const m = raw.match(re);
    return m ? m[1].trim() : '';
  };
  return {
    thumbnailText: get('THUMBNAIL_TEXT', 'VIDEO_TITLE'),
    videoTitle: get('VIDEO_TITLE', 'VIDEO_DESCRIPTION'),
    videoDescription: get('VIDEO_DESCRIPTION', 'HASHTAGS'),
    hashtags: get('HASHTAGS', '___END___'),
  };
};

const CopyButton = ({ text, label }) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };
  return (
    <button onClick={handleCopy} className="btn btn-ghost btn-sm ml-auto flex-shrink-0">
      <FiCopy size={14} />
      {label}
    </button>
  );
};

export const ContentEditor = () => {
  const { type } = useParams();
  const t = useT();

  const initialType = URL_TO_TYPE[type] || 'anchor_script';

  const [formData, setFormData] = useState({
    contentType: initialType,
    inputContent: '',
    tone: 'formal',
    duration: CONTENT_TYPES[initialType]?.duration || 400,
    customInstructions: '',
  });

  const [generatedContent, setGeneratedContent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [rating, setRating] = useState(0);
  const [budget, setBudget] = useState(null);

  const fetchBudget = async () => {
    try {
      const status = await budgetService.getBudgetStatus();
      setBudget(status);
    } catch {
      // silently ignore — budget banner is non-critical
    }
  };

  useEffect(() => {
    fetchBudget();
  }, []);

  useEffect(() => {
    const newType = URL_TO_TYPE[type] || 'anchor_script';
    setFormData(prev => ({
      ...prev,
      contentType: newType,
      duration: CONTENT_TYPES[newType]?.duration || 400,
    }));
    setGeneratedContent(null);
    setRating(0);
  }, [type]);

  const isYouTube = formData.contentType === 'youtube';
  const isFacebook = formData.contentType === 'facebook_post';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'contentType' &&
        CONTENT_TYPES[value] && {
          duration: CONTENT_TYPES[value].duration,
        }),
    }));
  };

  const handleGenerate = async () => {
    if (!formData.inputContent.trim()) {
      toast.error(t('toast.inputRequired'));
      return;
    }

    if (budget?.isExceeded) {
      toast.error(t('budget.dailyReached', { limit: budget.dailyLimit }));
      return;
    }

    setIsLoading(true);
    try {
      const result = await contentService.generateContent(formData);
      setGeneratedContent(result);
      setRating(0);
      toast.success(t('toast.generated'));
      fetchBudget();
    } catch (error) {
      const msg = error.response?.data?.message || t('toast.generateFailed');
      toast.error(msg);
      if (error.response?.status === 429) fetchBudget();
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyAll = () => {
    if (generatedContent?.outputs?.primaryOutput) {
      navigator.clipboard.writeText(generatedContent.outputs.primaryOutput);
      toast.success(t('toast.copied'));
    }
  };

  const handleDownload = () => {
    if (generatedContent?.outputs?.primaryOutput) {
      const element = document.createElement('a');
      element.setAttribute(
        'href',
        'data:text/plain;charset=utf-8,' +
          encodeURIComponent(generatedContent.outputs.primaryOutput)
      );
      element.setAttribute('download', `${formData.contentType}_${Date.now()}.txt`);
      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    }
  };

  const handleRate = async (score) => {
    if (!generatedContent?._id) return;
    setRating(score);
    try {
      await contentService.rateContent(generatedContent._id, { score, feedback: '' });
      toast.success(t('toast.rated'));
    } catch (error) {
      toast.error(t('toast.rateFailed'));
    }
  };

  const ytSections = isYouTube && generatedContent
    ? parseYouTubeOutput(generatedContent.outputs?.primaryOutput)
    : null;

  const BudgetBanner = () => {
    if (!budget) return null;
    const { spent, dailyLimit, remaining, percentUsed, isExceeded, isNearLimit } = budget;

    let colors, Icon, message;
    if (isExceeded) {
      colors = 'bg-red-50 border-red-300 text-red-800';
      Icon = FiAlertCircle;
      message = t('budget.exceeded', { limit: dailyLimit, spent: spent.toFixed(4) });
    } else if (percentUsed >= 90) {
      colors = 'bg-orange-50 border-orange-300 text-orange-800';
      Icon = FiAlertTriangle;
      message = t('budget.near90', { remaining: remaining.toFixed(4), limit: dailyLimit, percent: percentUsed });
    } else if (isNearLimit) {
      colors = 'bg-yellow-50 border-yellow-300 text-yellow-800';
      Icon = FiAlertTriangle;
      message = t('budget.near', { remaining: remaining.toFixed(4), limit: dailyLimit, percent: percentUsed });
    } else {
      colors = 'bg-green-50 border-green-200 text-green-800';
      Icon = null;
      message = null;
    }

    if (!message) return null;

    return (
      <div className={`flex items-start gap-3 px-4 py-3 rounded-lg border ${colors}`}>
        {Icon && <Icon size={18} className="flex-shrink-0 mt-0.5" />}
        <div className="flex-1 text-sm">
          <span>{message}</span>
          {(isExceeded || isNearLimit) && (
            <span className="ml-2 font-medium">{t('budget.contactAdmin')}</span>
          )}
        </div>
        <div className="flex-shrink-0 text-xs font-medium opacity-70">
          ${spent.toFixed(4)} / ${dailyLimit}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <BudgetBanner />
      {/* Input Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">{t('editor.configuration')}</h2>

          {/* Content Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('editor.contentType')}
            </label>
            <select
              name="contentType"
              value={formData.contentType}
              onChange={handleInputChange}
              className="input"
            >
              {Object.entries(CONTENT_TYPES).map(([key, value]) => (
                <option key={key} value={key}>
                  {t(value.labelKey)}
                </option>
              ))}
            </select>
          </div>

          {/* Tone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('editor.tone')}
            </label>
            <select
              name="tone"
              value={formData.tone}
              onChange={handleInputChange}
              className="input"
            >
              {TONES.map((tone) => (
                <option key={tone} value={tone}>
                  {t(`tone.${tone}`)}
                </option>
              ))}
            </select>
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('editor.duration')}
            </label>
            <input
              type="number"
              name="duration"
              value={formData.duration}
              onChange={handleInputChange}
              className="input"
              min="1"
              max="10000"
            />
          </div>

          {/* Custom Instructions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('editor.customInstructions')}
            </label>
            <textarea
              name="customInstructions"
              value={formData.customInstructions}
              onChange={handleInputChange}
              placeholder={t('editor.customPlaceholder')}
              className="input"
              rows="4"
            />
          </div>

          <button
            onClick={handleGenerate}
            disabled={isLoading}
            className="btn btn-primary w-full"
          >
            {isLoading ? t('editor.generating') : t('editor.generate')}
          </button>
        </div>

        {/* Content Input */}
        <div className="lg:col-span-2">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('editor.inputContent')}</h2>
          <textarea
            name="inputContent"
            value={formData.inputContent}
            onChange={handleInputChange}
            placeholder={t('editor.inputPlaceholder')}
            className="input min-h-96"
          />
          <p className="text-sm text-gray-500 mt-2">{t('editor.minChars')}</p>
        </div>
      </div>

      {/* Generated Content Section */}
      {generatedContent && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{t('editor.generatedContent')}</h2>
              <p className="text-sm text-gray-500">
                {t('editor.tokens')}: {generatedContent.tokenUsage?.totalTokens} | {t('editor.cost')}: $
                {generatedContent.costUSD?.toFixed(4)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handleCopyAll} className="btn btn-ghost btn-sm">
                <FiCopy size={16} />
                {t('editor.copy')}
              </button>
              <button onClick={handleDownload} className="btn btn-ghost btn-sm">
                <FiDownload size={16} />
                {t('editor.download')}
              </button>
              <button onClick={handleGenerate} className="btn btn-ghost btn-sm">
                <FiRefreshCw size={16} />
                {t('editor.regenerate')}
              </button>
            </div>
          </div>

          {/* YouTube structured output */}
          {isYouTube && ytSections ? (
            <div className="space-y-4">
              {/* Thumbnail Text */}
              <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-red-700 uppercase tracking-wide">
                    {t('youtube.thumbnailText')}
                  </span>
                  <CopyButton text={ytSections.thumbnailText} label={t('editor.copy')} />
                </div>
                <p className="text-gray-900 font-bold text-lg leading-snug">
                  {ytSections.thumbnailText}
                </p>
              </div>

              {/* Video Title */}
              <div className="bg-orange-50 border border-orange-100 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-orange-700 uppercase tracking-wide">
                    {t('youtube.videoTitle')}
                  </span>
                  <CopyButton text={ytSections.videoTitle} label={t('editor.copy')} />
                </div>
                <p className="text-gray-900 font-semibold leading-snug">
                  {ytSections.videoTitle}
                </p>
                {ytSections.videoTitle && (
                  <p className="text-xs text-gray-400 mt-1">
                    {ytSections.videoTitle.length}/100 characters
                  </p>
                )}
              </div>

              {/* Video Description */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    {t('youtube.videoDescription')}
                  </span>
                  <CopyButton text={ytSections.videoDescription} label={t('editor.copy')} />
                </div>
                <p className="text-gray-900 whitespace-pre-wrap leading-relaxed text-sm">
                  {ytSections.videoDescription}
                </p>
              </div>

              {/* Hashtags */}
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-blue-700 uppercase tracking-wide">
                    {t('youtube.hashtags')}
                  </span>
                  <CopyButton text={ytSections.hashtags} label={t('editor.copy')} />
                </div>
                <div className="flex flex-wrap gap-2">
                  {ytSections.hashtags.split(/\s+/).filter(Boolean).map((tag, i) => (
                    <span
                      key={i}
                      className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* Default output display (Facebook Post, Anchor Script, Story, etc.) */
            <div
              className={`bg-gray-50 p-4 rounded-lg min-h-48 max-h-96 overflow-y-auto mb-4 font-sans ${
                isFacebook ? 'border-l-4 border-blue-500' : ''
              }`}
            >
              {isFacebook && (
                <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-200">
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold">
                    K
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{t('facebook.previewTitle')}</p>
                    <p className="text-xs text-gray-500">{t('facebook.previewSubtitle')}</p>
                  </div>
                </div>
              )}
              <p className="text-gray-900 whitespace-pre-wrap leading-relaxed">
                {generatedContent.outputs?.primaryOutput}
              </p>
            </div>
          )}

          {/* Rating */}
          <div className="flex items-center gap-4 mt-4">
            <span className="text-sm font-medium text-gray-700">{t('editor.rate')}</span>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => handleRate(star)}
                  className="transition"
                >
                  <FiStar
                    size={20}
                    className={
                      star <= rating
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-300'
                    }
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
